import { Analytics, PageEnvelope } from "@skv/core";
import { OpportunitySearchQueryState, useViewModel } from "@skv/state";
import { pluraliser } from "@skv/util";
import * as React from "react";
import { Alert, AlertProps } from "react-bootstrap";

import {
  ResultCard,
  ResultCardHeader,
  ResultCardVolunteerSearchLinkHeading,
  ResultCardVolunteerSearchLinkHeadingProps,
  ResultMetaList,
  ResultMetaListItem,
} from "~/ui/ResultCard";

import { useUserDispatch } from "~/state";

interface SearchSubscriptionProps {
  to: ResultCardVolunteerSearchLinkHeadingProps["to"];
  title: string;
  location: string;
  categories: string | null;
  dayTime: string | null;
  durations: string | null;
  keywords: string | null;
  organisation: string | null;
  suitableFor: string | null;
  typeOfWork: string | null;
  searchCriteria: OpportunitySearchQueryState;
  isSubscribed: boolean;
  onSubscribe: (e: React.FormEvent<HTMLFormElement>) => void;
  onDelete: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const SearchSubscription: React.FC<SearchSubscriptionProps> = ({
  to,
  title,
  categories,
  dayTime,
  durations,
  keywords,
  organisation,
  suitableFor,
  typeOfWork,
  location,
  searchCriteria,
  isSubscribed,
  onSubscribe,
  onDelete,
}) => {
  return (
    <ResultCard className="py-4 border-bottom">
      <ResultCardHeader>
        <ResultCardVolunteerSearchLinkHeading
          className="h2"
          title={title}
          to={to}
          stretchedLink={false}
          searchQuery={searchCriteria}
          shouldUpdateSearchForm
        />
      </ResultCardHeader>
      <ResultMetaList>
        {!!organisation && (
          <ResultMetaListItem className="mb-0">
            Organisation: <strong>{organisation}</strong>
          </ResultMetaListItem>
        )}
        {!!keywords && (
          <ResultMetaListItem className="mb-0">
            What: <strong>{keywords}</strong>
          </ResultMetaListItem>
        )}
        {!!categories && (
          <ResultMetaListItem className="mb-0">
            Categories: <strong>{categories}</strong>
          </ResultMetaListItem>
        )}
        {!!typeOfWork && (
          <ResultMetaListItem className="mb-0">
            Type of Work: <strong>{typeOfWork}</strong>
          </ResultMetaListItem>
        )}
        {!!suitableFor && (
          <ResultMetaListItem className="mb-0">
            Suitable For: <strong>{suitableFor}</strong>
          </ResultMetaListItem>
        )}
        {!!durations && (
          <ResultMetaListItem className="mb-0">
            Commitment: <strong>{durations}</strong>
          </ResultMetaListItem>
        )}
        {!!dayTime && (
          <ResultMetaListItem className="mb-0">
            Day &amp; Time: <strong>{suitableFor}</strong>
          </ResultMetaListItem>
        )}
        {!!location && (
          <ResultMetaListItem className="mb-0">
            Where: <strong>{location}</strong>
          </ResultMetaListItem>
        )}
      </ResultMetaList>

      {isSubscribed ? (
        <form action="/" method="POST" onSubmit={onSubscribe}>
          <button className="btn btn-link font-weight-bold ml-n2" type="submit">
            <svg
              className="mt-n1 mr-2"
              width="16"
              height="16"
              aria-hidden="true"
              focusable="false"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
            >
              <path
                fill="currentColor"
                d="M502.3 190.8c3.9-3.1 9.7-.2 9.7 4.7V400c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V195.6c0-5 5.7-7.8 9.7-4.7 22.4 17.4 52.1 39.5 154.1 113.6 21.1 15.4 56.7 47.8 92.2 47.6 35.7.3 72-32.8 92.3-47.6 102-74.1 131.6-96.3 154-113.7zM256 320c23.2.4 56.6-29.2 73.4-41.4 132.7-96.3 142.8-104.7 173.4-128.7 5.8-4.5 9.2-11.5 9.2-18.9v-19c0-26.5-21.5-48-48-48H48C21.5 64 0 85.5 0 112v19c0 7.4 3.4 14.3 9.2 18.9 30.6 23.9 40.7 32.4 173.4 128.7 16.8 12.2 50.2 41.8 73.4 41.4z"
              ></path>
            </svg>
            Unsubscribe
          </button>
        </form>
      ) : (
        <form action="/" method="POST" onSubmit={onSubscribe}>
          <button className="btn btn-link font-weight-bold ml-n2" type="submit">
            <svg
              className="mt-n1 mr-2"
              width="16"
              height="16"
              aria-hidden="true"
              focusable="false"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
            >
              <path
                fill="currentColor"
                d="M464 64H48C21.49 64 0 85.49 0 112v288c0 26.51 21.49 48 48 48h416c26.51 0 48-21.49 48-48V112c0-26.51-21.49-48-48-48zm0 48v40.805c-22.422 18.259-58.168 46.651-134.587 106.49-16.841 13.247-50.201 45.072-73.413 44.701-23.208.375-56.579-31.459-73.413-44.701C106.18 199.465 70.425 171.067 48 152.805V112h416zM48 400V214.398c22.914 18.251 55.409 43.862 104.938 82.646 21.857 17.205 60.134 55.186 103.062 54.955 42.717.231 80.509-37.199 103.053-54.947 49.528-38.783 82.032-64.401 104.947-82.653V400H48z"
              ></path>
            </svg>
            Subscribe
          </button>
          <button
            className="btn btn-link font-weight-bold"
            type="button"
            onClick={onDelete}
          >
            <svg
              className="mt-n1 mr-2"
              width="16"
              height="16"
              aria-hidden="true"
              focusable="false"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
            >
              <path
                fill="currentColor"
                d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z"
              ></path>
            </svg>
            Delete
          </button>
        </form>
      )}
    </ResultCard>
  );
};

interface SubscriptionsViewModel {
  subscriptionCount: number;
  subscriptions: Subscription[];
}

interface Subscription {
  id: number;
  title: string;
  isSubscribed: boolean;
  keyword: string | null;
  location: string;
  organisation: string;
  categories: string[];
  typeOfWork: string[];
  suitableFor: string[];
  durations: string[];
  dayTime: string[];
  url: string;

  searchCriteria: OpportunitySearchQueryState;
}

export const Subscriptions = () => {
  const alertRef = React.useRef<HTMLDivElement>();
  const [alertState, setAlertState] = React.useState<AlertProps>({
    show: false,
    variant: null,
    children: null,
    dismissible: true,
  });

  const { updateUser } = useUserDispatch();
  const [{ data, error, status }, { updateViewModel }] =
    useViewModel<SubscriptionsViewModel>();

  const createSubscribeHandler =
    (idx: number) =>
    (e: React.FormEvent<HTMLFormElement>): void => {
      e.preventDefault();
      const nextViewModel = updateViewModel((prev) => {
        const nextSubscribed = !prev.subscriptions[idx].isSubscribed;
        return {
          ...prev,
          subscriptions: Object.assign([], prev.subscriptions, {
            [idx]: {
              ...prev.subscriptions[idx],
              isSubscribed: nextSubscribed,
            },
          }),
          subscriptionCount: nextSubscribed
            ? prev.subscriptionCount + 1
            : prev.subscriptionCount - 1,
        };
      });

      const subscribed = nextViewModel.subscriptions[idx].isSubscribed;

      fetch("/my-activity/subscription", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          subscriptionId: nextViewModel.subscriptions[idx].id,
          subscribed,
        }),
      })
        .then<
          PageEnvelope<{
            subscribed: boolean;
            subscriptionId: number;
          }>
        >((res) => {
          if (res.ok) {
            return res.json();
          }

          throw Error("");
        })
        .then(({ success, data, errorCode, errorMessage }) => {
          if (success) {
            updateUser((prev) => ({
              ...prev,
              emailAlertCount: subscribed
                ? prev.emailAlertCount + 1
                : prev.emailAlertCount - 1,
            }));
            return;
          } else {
            setAlertState({
              show: true,
              variant: "danger",
              children: errorMessage ?? "An unexpected error occurred",
            });
          }
        });
    };

  const createDeleteHandler =
    (idx: number, subscriptionId: number) =>
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
      const nextViewModel = updateViewModel((prev) => ({
        ...prev,
        subscriptions: prev.subscriptions.filter((_, _idx) => idx !== _idx),
      }));

      fetch(`/my-activity/subscription/${subscriptionId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
        .then((res) => {
          if (res.ok) {
            return res.json();
          }

          throw Error("");
        })
        .then(({ success, data, errorCode, errorMessage }) => {
          if (success) {
            return;
          }
        });
    };

  if (status === "loading") {
    return <p className="mt-3">Please wait, loading...</p>;
  }

  return (
    <>
      {/* TODO: create custom class? .myactivity-heading */}
      <h2
        className="display-3 font-weight-bold border-bottom mt-n2 mb-0"
        css={`
          padding-bottom: 1.25rem;
        `}
      >
        {pluraliser(data.subscriptionCount, {
          none:
            data.subscriptions.length === 0
              ? "You have not subscribed to any email alerts"
              : "You have no active email alerts",
          plural: `You have ${data.subscriptionCount} email alerts`,
          singular: "You have 1 email alert",
        })}
      </h2>

      <Alert ref={alertRef} {...alertState} />

      {data.subscriptions.length > 0 && (
        <ul className="list-unstyled">
          {data.subscriptions.map((n, idx) => (
            <li key={n.id} className="my-0">
              <SearchSubscription
                title={n.title}
                location={n.location}
                to={n.url}
                categories={n.categories.join(", ")}
                dayTime={n.dayTime.join(", ")}
                durations={n.durations.join(", ")}
                keywords={n.keyword}
                organisation={n.organisation}
                suitableFor={n.suitableFor.join(", ")}
                typeOfWork={n.typeOfWork.join(", ")}
                searchCriteria={n.searchCriteria}
                isSubscribed={n.isSubscribed}
                onSubscribe={createSubscribeHandler(idx)}
                onDelete={createDeleteHandler(idx, n.id)}
              />
            </li>
          ))}
        </ul>
      )}

      <footer className="mt-5">
        {/* TODO: Apply correct style for h3 */}
        <h3 className="display-4 font-weight-bold">
          Can't find an application?
        </h3>
        <p>
          If you subscribed using a different email address, please sign in
          using that email address to see your subscriptions.
        </p>
      </footer>

      <Analytics
        data={{
          subSection: "volunteers",
          subSubSection: "activity",
          subSubSubSection: "subscriptions",
          pageTitle: "subscriptions",
          loginStatus: "loggedin",
        }}
      />
    </>
  );
};
